import { create } from "zustand";

/**
 * GLOBAL STATE MANAGEMENT (Zustand)
 *
 * Manages all application state:
 * - Authentication
 * - User data and balance
 * - Current round state
 * - Bet state
 * - Game history
 * - Connection status
 */

export interface User {
  id: string;
  username: string;
  balance: number;
}

export interface Round {
  roundId: string;
  roundNumber: number;
  phase: "BETTING" | "RUNNING" | "CRASHED" | "COMPLETE";
  commitment: string;
  currentMultiplier: number;
  bettingStartTime?: number;
  bettingDuration?: number;
  startTime?: number;
  crashPoint?: number;
  crashTime?: number;
}

export interface Bet {
  betId: string;
  amount: number;
  autoCashoutAt?: number;
  status: "pending" | "active" | "cashed_out" | "lost";
  cashedOutMultiplier?: number;
  payout?: number;
  profit?: number;
}

export interface HistoricalRound {
  id: string;
  roundNumber: number;
  crashPointRevealed: number;
  createdAt: string;
}

export interface PlayerActivity {
  username: string;
  action: "bet" | "cashout";
  amount: number;
  multiplier?: number;
  timestamp: number;
}

interface GameState {
  // Authentication
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;

  // User
  user: User | null;

  // Connection
  connectionState: "disconnected" | "connecting" | "connected" | "error";

  // Current round
  currentRound: Round | null;

  // Current bet
  currentBet: Bet | null;

  // History
  roundHistory: HistoricalRound[];
  betHistory: Array<Bet & { roundNumber: number }>;

  // Live activity
  playerActivity: PlayerActivity[];

  // UI state
  showLoginModal: boolean;
  showRegisterModal: boolean;
  error: string | null;

  // Actions
  setAuth: (
    tokens: { accessToken: string; refreshToken: string },
    user: User,
  ) => void;
  logout: () => void;
  updateBalance: (balance: number) => void;
  setConnectionState: (state: GameState["connectionState"]) => void;

  // Round actions
  updateRound: (round: Partial<Round>) => void;
  setRoundPhase: (phase: Round["phase"]) => void;
  updateMultiplier: (multiplier: number) => void;
  crashRound: (crashPoint: number, crashTime: number) => void;
  resetRound: () => void;

  // Bet actions
  placeBet: (bet: Bet) => void;
  cashoutBet: (result: {
    payout: number;
    profit: number;
    multiplier: number;
    balance: number;
  }) => void;
  clearBet: () => void;

  // History actions
  addRoundToHistory: (round: HistoricalRound) => void;
  setHistory: (data: {
    rounds: HistoricalRound[];
    bets: Array<Bet & { roundNumber: number }>;
  }) => void;

  // Activity actions
  addPlayerActivity: (activity: Omit<PlayerActivity, "timestamp">) => void;

  // UI actions
  setShowLoginModal: (show: boolean) => void;
  setShowRegisterModal: (show: boolean) => void;
  setError: (error: string | null) => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  // Initial state
  isAuthenticated: false,
  accessToken: localStorage.getItem("accessToken"),
  refreshToken: localStorage.getItem("refreshToken"),
  user: null,
  connectionState: "disconnected",
  currentRound: null,
  currentBet: null,
  roundHistory: [],
  betHistory: [],
  playerActivity: [],
  showLoginModal: false,
  showRegisterModal: false,
  error: null,

  // Auth actions
  setAuth: (tokens, user) => {
    localStorage.setItem("accessToken", tokens.accessToken);
    localStorage.setItem("refreshToken", tokens.refreshToken);

    set({
      isAuthenticated: true,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      user: {
        ...user,
        balance:
          typeof user.balance === "string"
            ? parseFloat(user.balance)
            : user.balance,
      },
      showLoginModal: false,
      showRegisterModal: false,
    });
  },

  logout: () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    set({
      isAuthenticated: false,
      accessToken: null,
      refreshToken: null,
      user: null,
      currentBet: null,
    });
  },

  updateBalance: (balance) => {
    const { user } = get();
    if (user) {
      set({
        user: {
          ...user,
          balance: typeof balance === "string" ? parseFloat(balance) : balance,
        },
      });
    }
  },

  setConnectionState: (connectionState) => {
    set({ connectionState });
  },

  // Round actions
  updateRound: (round) => {
    const { currentRound } = get();
    set({
      currentRound: currentRound
        ? { ...currentRound, ...round }
        : (round as Round),
    });
  },

  setRoundPhase: (phase) => {
    const { currentRound } = get();
    if (currentRound) {
      set({
        currentRound: { ...currentRound, phase },
      });
    }
  },

  updateMultiplier: (multiplier) => {
    const { currentRound } = get();
    if (currentRound) {
      set({
        currentRound: { ...currentRound, currentMultiplier: multiplier },
      });
    }
  },

  crashRound: (crashPoint, crashTime) => {
    const { currentRound, currentBet } = get();
    if (currentRound) {
      set({
        currentRound: {
          ...currentRound,
          phase: "CRASHED",
          crashPoint,
          crashTime,
        },
      });

      // Mark bet as lost if not cashed out
      if (currentBet && currentBet.status === "active") {
        set({
          currentBet: {
            ...currentBet,
            status: "lost",
          },
        });
      }
    }
  },

  resetRound: () => {
    set({
      currentRound: null,
      currentBet: null,
    });
  },

  // Bet actions
  placeBet: (bet) => {
    set({ currentBet: bet });
  },

  cashoutBet: (result) => {
    const { currentBet, updateBalance } = get();
    if (currentBet) {
      set({
        currentBet: {
          ...currentBet,
          status: "cashed_out",
          cashedOutMultiplier: result.multiplier,
          payout: result.payout,
          profit: result.profit,
        },
      });

      updateBalance(result.balance);
    }
  },

  clearBet: () => {
    set({ currentBet: null });
  },

  // History actions
  addRoundToHistory: (round) => {
    const { roundHistory } = get();
    set({
      roundHistory: [round, ...roundHistory].slice(0, 50), // Keep last 50
    });
  },

  setHistory: (data) => {
    set({
      roundHistory: data.rounds,
      betHistory: data.bets as Array<Bet & { roundNumber: number }>,
    });
  },

  // Activity actions
  addPlayerActivity: (activity) => {
    const { playerActivity } = get();
    const newActivity: PlayerActivity = {
      ...activity,
      timestamp: Date.now(),
    };

    set({
      playerActivity: [newActivity, ...playerActivity].slice(0, 20), // Keep last 20
    });
  },

  // UI actions
  setShowLoginModal: (show) => {
    set({ showLoginModal: show });
  },

  setShowRegisterModal: (show) => {
    set({ showRegisterModal: show });
  },

  setError: (error) => {
    set({ error });

    // Clear error after 5 seconds
    if (error) {
      setTimeout(() => {
        const { error: currentError } = get();
        if (currentError === error) {
          set({ error: null });
        }
      }, 5000);
    }
  },
}));
